import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import dbService from "../../services/database";
import { SearchContext } from "../../context/SearchContext";
import { AuthContext } from "../../context/AuthContext";
import "./datatable.scss";
import { userColumns } from "../../datatablesource";
import useRolePermissions from "../../hooks/useRolePermissions";
// Import Material-UI icons for clean action buttons
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EditIcon from "@mui/icons-material/Edit";

const Datatable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const { search } = useContext(SearchContext);
  const { currentUser, isLoading: authLoading } = useContext(AuthContext);
  const pageSize = 10; // Adjust based on your UI design
  const { isReadOnlyRole } = useRolePermissions();

  useEffect(() => {
    // Wait for auth to be ready before fetching data
    if (authLoading) {
      console.log("⏳ Waiting for auth to be ready...");
      return;
    }

    if (!currentUser) {
      console.log("⚠️ No authenticated user, skipping data fetch");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        console.log("🔄 Fetching users (auth ready)...");

        // Fetch users from PostgreSQL using correct parameter format
        const result = await dbService.getUsers({
          search,
          limit: pageSize,
          orderBy: "display_name",
          order: "asc",
        });

        if (result.error) {
          console.error("Error fetching users:", result.error);
          return;
        }

        const users = result.data || [];
        setData(users);
        console.log(`📊 Loaded ${users.length} users from PostgreSQL API`);
      } catch (error) {
        console.error("Error in data fetch:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [search, authLoading, currentUser]); // Add auth dependencies

  const approveUser = async (userId) => {
    try {
      await dbService.updateUser(userId, {
        status: "approved",
      });

      // Refresh data
      const result = await dbService.getUsers({
        search,
        limit: pageSize,
        orderBy: "display_name",
        order: "asc",
      });
      setData(result.data || []);
    } catch (error) {
      console.error("Error approving user:", error);
      alert("Failed to approve user. Please try again.");
    }
  };

  const handleDelete = async (userId) => {
    if (isReadOnlyRole) {
      return; // Don't allow deletion for read-only roles
    }

    // Find user name for better user experience
    const user = data.find((item) => item.id === userId);
    const userName = user?.display_name || user?.displayName || "Unknown User";

    const confirmed = window.confirm(
      `Are you sure you want to delete "${userName}"?\n\n` +
        `This will permanently remove:\n` +
        `• User profile and personal information\n` +
        `• Transaction history (purchases/sales)\n` +
        `• Dividend payment records\n\n` +
        `This action cannot be undone!`
    );

    if (!confirmed) {
      return;
    }

    try {
      console.log(`🗑️ Attempting to delete user: ${userName} (${userId})`);

      await dbService.deleteUser(userId);

      // If successful, remove from local state and show success message
      setData(data.filter((item) => item.id !== userId));

      alert(`✅ User "${userName}" has been successfully deleted.`);
      console.log(`✅ Successfully deleted user: ${userName}`);
    } catch (error) {
      console.error("❌ Error deleting user:", error);

      // Handle different types of errors with appropriate user messages
      let errorMessage = "Failed to delete user. Please try again.";

      if (error.response?.status === 400) {
        // Backend returned a 400 error (constraint violation)
        const backendError = error.response.data?.error;
        if (backendError && backendError.includes("transfer record")) {
          errorMessage =
            `❌ Cannot Delete User\n\n` +
            `"${userName}" cannot be deleted because they have transfer history.\n\n` +
            `Users with transfer records cannot be removed to maintain data integrity.\n\n` +
            `If you need to remove this user, please contact the system administrator.`;
        } else {
          errorMessage =
            `❌ Cannot Delete User\n\n` +
            `"${userName}" cannot be deleted due to existing related records.\n\n` +
            `${backendError || "Please contact administrator for assistance."}`;
        }
      } else if (error.response?.status === 404) {
        errorMessage =
          `❌ User Not Found\n\n` +
          `"${userName}" was not found in the system.\n\n` +
          `They may have already been deleted by another user.`;
        // Remove from local state since user doesn't exist
        setData(data.filter((item) => item.id !== userId));
      } else if (error.response?.status >= 500) {
        errorMessage =
          `❌ Server Error\n\n` +
          `There was a problem with the server while trying to delete "${userName}".\n\n` +
          `Please try again later or contact technical support.`;
      } else {
        // Generic error handling
        const backendMessage = error.response?.data?.error || error.message;
        errorMessage =
          `❌ Delete Failed\n\n` +
          `Could not delete "${userName}".\n\n` +
          `Error: ${backendMessage}`;
      }

      alert(errorMessage);
    }
  };

  const exportToCsv = async () => {
    setLoading(true);
    console.log("🔄 Starting CSV export...");

    const result = await dbService.getAllUsersForExport(); // Get all users with specific fields only

    const allUsersData = result.data;
    if (result.error || !allUsersData || allUsersData.length === 0) {
      console.error("Error fetching users for export:", result.error);
      alert("Failed to export data. Please try again.");
      setLoading(false);
      return;
    }

    console.log(`✅ Retrieved ${allUsersData.length} users for export`);

    // Define the specific fields we want to export in the desired order
    const exportFields = [
      "display_name",
      "email",
      "address",
      "phone",
      "share_amount",
      "share_value",
      "status",
      // 'membership_code',
      "ghana_card",
      "next_of_kin",
      "account_number",
      "payment_channel",
      "inputter",
      "dob",
      "gender",
      "kin_contact",
      "beneficiary_type",
      // 'corp_name',
      // 'doi',
      // 'tax_id',
      // 'date_of_birth',
      "date_of_purchase",
      "digital_address",
      "next_of_kin_contact",
    ];

    // CSV export logic with data cleaning
    const csvRows = [];
    csvRows.push(exportFields.join(","));

    for (const row of allUsersData) {
      const values = exportFields.map((field) => {
        let value = row[field] || ""; // Get field value or empty string

        // Clean and format the data
        value = value.toString().trim(); // Remove leading/trailing spaces

        // Escape quotes and wrap in quotes
        const escaped = value.replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(","));
    }

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `shareholders-data-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log("✅ CSV export completed successfully");
    setLoading(false);
  };

  const actionColumn = [
    {
      field: "action",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => {
        return (
          <div className="cellAction">
            <Link
              to={`/users/${params.row.id}`}
              style={{ textDecoration: "none" }}
              title="View Details"
            >
              <div className="viewButton">
                <VisibilityIcon />
              </div>
            </Link>
            {!isReadOnlyRole && (
              <>
                <Link
                  to={`/users/edit/${params.row.id}`}
                  style={{ textDecoration: "none" }}
                  title="Edit User"
                >
                  <div className="editButton">
                    <EditIcon />
                  </div>
                </Link>
                <Link
                  to={`/users/${params.row.id}/transfer`}
                  style={{ textDecoration: "none" }}
                  className={isReadOnlyRole ? "disabled-link" : ""}
                  onClick={(e) => isReadOnlyRole && e.preventDefault()}
                  title="Transfer Shares"
                >
                  <div
                    className={`transferButton ${
                      isReadOnlyRole ? "disabled" : ""
                    }`}
                  >
                    <SwapHorizIcon />
                  </div>
                </Link>
                <div
                  className={`deleteButton ${isReadOnlyRole ? "disabled" : ""}`}
                  onClick={() => !isReadOnlyRole && handleDelete(params.row.id)}
                  title="Delete User"
                >
                  <DeleteIcon />
                </div>
              </>
            )}
          </div>
        );
      },
    },
  ];

  const filteredData = data; // Server-side filtering makes client-side filtering redundant

  return (
    <div className="datatable">
      {loading ? (
        <div className="loadingState">
          <div className="professional-spinner">
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-center"></div>
          </div>
          <p>Loading shareholders...</p>
        </div>
      ) : (
        <>
          <div className="datatableTitle">
            Shareholders
            {!isReadOnlyRole && (
              <Link to="/users/new" className="link">
                Add New
              </Link>
            )}
            <button onClick={exportToCsv} className="exportButton">
              Export to CSV
            </button>
          </div>
          <DataGrid
            className="datagrid"
            rows={filteredData}
            columns={userColumns.concat(actionColumn)}
            pageSize={9}
            rowsPerPageOptions={[9]}
            checkboxSelection
          />
        </>
      )}
    </div>
  );
};

export default Datatable;
